import { Inject, Injectable } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { NavigationEnd, Router } from '@angular/router';
import { filter, map, mapTo, pluck, switchMap } from 'rxjs/operators';
import { TranslatorService } from '../translator';
import { BehaviorSubject, Observable, combineLatest, of } from 'rxjs';
import { Environment } from '@sc/data-access-models';
import { ENV_CONFIG } from '@sc/data-access-environment';

export interface TranslationParams {
  key: string;
  params?: Record<string, any>;
}

/**
 * TODO - After upgrading to angular 14, change this service to extend TitleStrategy from @angular/router and provide it alongside router module
 * @see https://angular.io/guide/router#setting-the-page-title
 */
@Injectable({ providedIn: 'root' })
export class TabTitleStrategyService {
  public static readonly TAB_TITLE_KEY = 'tabTitle';
  public static readonly TAB_PAGINATION_KEY = 'tabPagination';

  private readonly appTitle$ = new BehaviorSubject<TranslationParams>(null);
  private readonly pageTitle$ = new BehaviorSubject<TranslationParams>(null);
  private readonly pagination$ = new BehaviorSubject<TranslationParams>(null);
  private readonly stepperInfos$ = new BehaviorSubject<TranslationParams>(null);

  private removePaginationNextRoute = true;

  constructor(
    private readonly router: Router,
    private readonly titleService: Title,
    private readonly translator: TranslatorService,
    @Inject(ENV_CONFIG) private readonly env: Environment
  ) {
    this.appTitle$.next({
      key: `GlobalApplicativeConstants.tabTitles.appTitles.${this.env.appName}`,
    });

    // Observable to format and translate each part of the tab title from service subjects
    combineLatest([
      this.appTitle$.pipe(filter((appTitle) => !!appTitle?.key)),
      this.pageTitle$.pipe(filter((pageTitle) => !!pageTitle?.key)),
      this.pagination$,
      this.stepperInfos$,
    ])
      .pipe(
        switchMap(([appTitle, pageTitle, pagination, stepperInfos]) =>
          combineLatest([
            this.translator.getTranslation(appTitle.key, appTitle.params),
            this.translator.getTranslation(pageTitle.key, pageTitle.params),
            pagination?.key
              ? this.translator.getTranslation(
                  pagination.key,
                  pagination.params
                )
              : of(null),
            stepperInfos?.key
              ? this.translator.getTranslation(
                  stepperInfos?.key,
                  stepperInfos?.params
                )
              : of(null),
          ])
        ),
        switchMap(([appTitle, pageTitle, pagination, stepperInfos]) => {
          if (pagination) {
            return this.translator.getTranslation(
              'GlobalApplicativeConstants.tabTitles.baseFormatWithPagination',
              {
                appName: appTitle,
                pageName: pageTitle,
                pagination: pagination,
              }
            );
          }
          if (stepperInfos) {
            return this.translator.getTranslation(
              'GlobalApplicativeConstants.tabTitles.baseFormatWithStepper',
              {
                appName: appTitle,
                pageName: pageTitle,
                stepperInfos: stepperInfos,
              }
            );
          }

          return this.translator.getTranslation(
            'GlobalApplicativeConstants.tabTitles.baseFormat',
            { appName: appTitle, pageName: pageTitle }
          );
        })
      )
      .subscribe((translatedTabTitle) => {
        if (
          typeof translatedTabTitle === 'string' &&
          !translatedTabTitle.includes('GlobalApplicativeConstants')
        ) {
          this.titleService.setTitle(translatedTabTitle);
        }
      });

    // Observable triggering after each route change
    const routeChange$ = this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd)
    ) as Observable<NavigationEnd>;

    // Reset pagination subject after each route change, if asked to
    routeChange$
      .pipe(filter(() => this.removePaginationNextRoute))
      .subscribe(() => this.pagination$.next(null));

    // After each route change, search in the route children to retrieve the most precise title and pagination we can from route config data
    routeChange$
      .pipe(
        mapTo(this.router.routerState.root),
        pluck('snapshot'),
        map((routeSnapshot) => {
          let lastTitleFound = '';
          let lastPaginationFound = '';

          let usedRoute = routeSnapshot;
          do {
            if (usedRoute?.data?.[TabTitleStrategyService.TAB_TITLE_KEY]) {
              lastTitleFound =
                usedRoute.data[TabTitleStrategyService.TAB_TITLE_KEY];
            }

            if (usedRoute?.data?.[TabTitleStrategyService.TAB_PAGINATION_KEY]) {
              lastPaginationFound =
                usedRoute.data[TabTitleStrategyService.TAB_PAGINATION_KEY];
            }

            usedRoute = usedRoute?.firstChild;
          } while (usedRoute);

          return [lastTitleFound, lastPaginationFound] as [string, string];
        })
      )
      .subscribe(([newTabTitle, newTabPagination]) => {
        this.pageTitle$.next({ key: newTabTitle });
        this.pagination$.next({ key: newTabPagination });
      });
  }

  public setPagination(
    newPagination: TranslationParams,
    removeAfterRouteChange = true
  ) {
    this.pagination$.next(newPagination);
    this.removePaginationNextRoute = removeAfterRouteChange;
  }

  public setStepperInfos(newStepperInfo: TranslationParams) {
    this.stepperInfos$.next(newStepperInfo);
  }
}
